import { logger } from './logger';

/**
 * Validation utilities for PRD classification data.
 *
 * Ensures type safety and validates classification fields from API responses.
 */

const VALID_SCENARIO_TYPES = [
  'frontend_feature',
  'backend_api',
  'fullstack_feature',
  'fullstack_application',
  'bugfix',
  'refactor_tech_debt',
  'integration_third_party',
  'infra_devops',
  'data_pipeline_reporting',
  'ai_feature',
] as const;

const VALID_SIZES = ['task', 'feature', 'epic', 'initiative'] as const;

const VALID_CONFIDENCE_LEVELS = ['high', 'medium', 'low'] as const;

export type ConfidenceLevel = (typeof VALID_CONFIDENCE_LEVELS)[number];
export type ProjectSize = (typeof VALID_SIZES)[number];
export type ScenarioType = (typeof VALID_SCENARIO_TYPES)[number];

export interface ClassificationData {
  scenarioType: ScenarioType | null;
  projectSize: ProjectSize | null;
  confidence: ConfidenceLevel | null;
  reasoning: string | null;
}

/**
 * Validates and normalizes all classification fields from an API response.
 *
 * This function handles type safety by accepting `unknown` types from API responses,
 * which may be `undefined`, `null`, `string`, or other types. It normalizes all
 * invalid values to `null` and validates string values against allowed enums.
 *
 * **Always use this function** when processing classification data from API responses
 * to ensure type safety and prevent runtime errors.
 *
 * @param data - Raw classification data from API response (may contain undefined, null, or invalid types)
 * @returns Validated and normalized classification data with guaranteed types (null or valid enum values)
 */
export function validateClassificationData(data: {
  classification_confidence?: unknown;
  classification_reasoning?: unknown;
  project_size?: unknown;
  scenario_type?: unknown;
}): ClassificationData {
  return {
    scenarioType: validateScenarioType(data.scenario_type),
    projectSize: validateProjectSize(data.project_size),
    confidence: validateConfidenceLevel(data.classification_confidence),
    reasoning: validateReasoning(data.classification_reasoning),
  };
}

/**
 * Validates and normalizes confidence level from API response.
 *
 * @param value - Raw value from API (could be string, null, undefined, or other types)
 * @returns Valid confidence level or null
 */
export function validateConfidenceLevel(value: unknown): ConfidenceLevel | null {
  if (value === null || value === undefined) {
    return null;
  }

  if (typeof value !== 'string') {
    logger.warn(
      { component: 'classificationValidation' },
      'Invalid classification_confidence type, expected string',
      { value, type: typeof value },
    );
    return null;
  }

  const normalized = value.trim().toLowerCase();
  if (VALID_CONFIDENCE_LEVELS.includes(normalized as ConfidenceLevel)) {
    return normalized as ConfidenceLevel;
  }

  logger.warn(
    { component: 'classificationValidation' },
    'Invalid classification_confidence value',
    {
      value,
      normalized,
      validLevels: VALID_CONFIDENCE_LEVELS,
    },
  );
  return null;
}

/**
 * Validates and normalizes project size from API response.
 *
 * @param value - Raw value from API (could be string, null, undefined, or other types)
 * @returns Valid project size or null
 */
export function validateProjectSize(value: unknown): ProjectSize | null {
  if (value === null || value === undefined) {
    return null;
  }

  if (typeof value !== 'string') {
    logger.warn(
      { component: 'classificationValidation' },
      'Invalid project_size type, expected string',
      { value, type: typeof value },
    );
    return null;
  }

  const normalized = value.trim().toLowerCase();
  if (VALID_SIZES.includes(normalized as ProjectSize)) {
    return normalized as ProjectSize;
  }

  logger.warn({ component: 'classificationValidation' }, 'Invalid project_size value', {
    value,
    normalized,
    validSizes: VALID_SIZES,
  });
  return null;
}

/**
 * Validates and normalizes classification reasoning from API response.
 *
 * @param value - Raw value from API (could be string, null, undefined, or other types)
 * @returns Valid reasoning string or null
 */
export function validateReasoning(value: unknown): string | null {
  if (value === null || value === undefined) {
    return null;
  }

  if (typeof value !== 'string') {
    logger.warn(
      { component: 'classificationValidation' },
      'Invalid classification_reasoning type, expected string',
      { value, type: typeof value },
    );
    return null;
  }

  const trimmed = value.trim();
  return trimmed.length > 0 ? trimmed : null;
}

/**
 * Validates and normalizes scenario type from API response.
 *
 * @param value - Raw value from API (could be string, null, undefined, or other types)
 * @returns Valid scenario type or null
 */
export function validateScenarioType(value: unknown): ScenarioType | null {
  if (value === null || value === undefined) {
    return null;
  }

  if (typeof value !== 'string') {
    logger.warn(
      { component: 'classificationValidation' },
      'Invalid scenario_type type, expected string',
      { value, type: typeof value },
    );
    return null;
  }

  const normalized = value.trim().toLowerCase();
  if (VALID_SCENARIO_TYPES.includes(normalized as ScenarioType)) {
    return normalized as ScenarioType;
  }

  logger.warn({ component: 'classificationValidation' }, 'Invalid scenario_type value', {
    value,
    normalized,
    validTypes: VALID_SCENARIO_TYPES,
  });
  return null;
}
